import { loginTarget, saveAuthSession } from '@/lib/apiConfig';
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import Turnstile, { useTurnstile } from "@/components/Turnstile";
import { nativeRequestHeaders } from "@/lib/native";


export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const turnstile = useTurnstile();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const target = loginTarget(email);
      const res = await fetch(`${target.apiUrl}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...nativeRequestHeaders() },
        body: JSON.stringify({ email: email.trim(), password, full_name: name, ...(turnstile.token && { turnstile_token: turnstile.token }) })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Registration failed' }));
        throw new Error(data.error || 'Registration failed');
      }

      const data = await res.json();
      saveAuthSession(data.token, target.environment);

      window.location.href = '/';
    } catch (err) {
      setError(err.message || 'Registration failed');
      turnstile.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="max-w-md w-full border-2 border-slate-200">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="bg-black rounded-xl px-4 py-2">
              <BrandLogo alt="Seiko Sports Technologies" className="h-8 w-auto object-contain" />
            </div>
          </div>
          <CardTitle className="text-2xl">Create Account</CardTitle>
          <p className="text-slate-600 text-sm mt-1">
            For freelancers working with Seiko Sports Technologies. Verify your email and wait for administrator acceptance before accessing the portal.
          </p>
        </CardHeader>
        <CardContent className="p-6 pt-4">
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="username"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="next"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                minLength={8}
                maxLength={72}
              />
            </div>

            <label className="flex items-start gap-2 text-sm"><input type="checkbox" required className="mt-1" />I am a freelancer working with Seiko Sports Technologies and understand that access requires administrator acceptance.</label>

            <Turnstile turnstile={turnstile} />

            <Button
              type="submit"
              disabled={loading || turnstile.loading || (turnstile.enabled && !turnstile.token)}
              className="w-full h-11 bg-[rgb(var(--brand-yellow))] hover:bg-[rgb(var(--brand-yellow-hover))] text-black font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="text-center mt-4">
            <p className="text-sm text-slate-600">
              Already have an account?{" "}
              <a href="/login" className="hover:text-slate-900 underline">
                Sign In
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
